import React, { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { useAuthStore } from '../stores/authStores';

type LoginErrors = {
  cardNumber?: string;
  birthDate?: string;
  submit?: string;
};

const Login: React.FC = () => {
  const navigate = useNavigate();
  const setToken = useAuthStore((state) => state.setToken);
  const setUser = useAuthStore((state) => state.setUser);

  const [cardNumber, setCardNumber] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [errors, setErrors] = useState<LoginErrors>({});
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const nextErrors: LoginErrors = {};

    if (!cardNumber.trim()) {
      nextErrors.cardNumber = 'Nomor kartu wajib diisi.';
    }

    if (!birthDate) {
      nextErrors.birthDate = 'Tanggal lahir wajib diisi.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validate()) return;

    let issuedToken: string | null = null;

    try {
      setLoading(true);
      setErrors({});

      const response = await axios.post('login', {
        card_number: cardNumber.trim(),
        birth_date: birthDate,
      });

      issuedToken = response.data?.token;
      if (!issuedToken) {
        throw new Error('Token login tidak ditemukan pada respons server.');
      }

      localStorage.setItem('auth_token', issuedToken);

      const meResponse = await axios.get('me', {
        headers: {
          Authorization: `Bearer ${issuedToken}`,
        },
      });

      setUser(meResponse.data?.data ?? meResponse.data?.user ?? null);
      setToken(issuedToken);
      navigate('/home', { replace: true });
    } catch (error: any) {
      if (issuedToken) {
        localStorage.removeItem('auth_token');
        setToken(null);
        setUser(null);
      }

      const message = error.response?.data?.message;
      setErrors({
        submit: message || 'Login gagal. Silakan periksa data atau coba beberapa saat lagi.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#e8f7f8] sm:flex sm:items-center sm:justify-center sm:px-6 sm:py-10">
      <div className="mx-auto min-h-screen w-full max-w-md overflow-hidden bg-white shadow-xl sm:min-h-0 sm:rounded-3xl">
        <section className="bg-gradient-to-br from-[#006b70] via-[#008f91] to-[#18b9b8] px-7 pb-16 pt-10 text-center text-white">
          <div className="mx-auto mb-7 flex h-16 w-40 items-center justify-center rounded-2xl bg-white px-5 shadow-md">
            <img
              src="/images/bri.svg"
              alt="BRI Life"
              className="h-10 w-full object-contain"
            />
          </div>
          <h1 className="text-2xl font-bold">Selamat Datang</h1>
          <p className="mt-2 text-sm leading-6 text-white/90">
            Masuk menggunakan data kepesertaan BRI Life Anda.
          </p>
        </section>

        <section className="relative -mt-7 rounded-t-3xl bg-white px-6 pb-10 pt-8 sm:px-8">
          <div className="mb-7 text-center">
            <h2 className="text-xl font-bold text-slate-800">Login Akun</h2>
            <p className="mt-1 text-sm text-slate-500">
              Pastikan nomor kartu dan tanggal lahir sesuai data peserta.
            </p>
          </div>

          <form onSubmit={handleLogin} noValidate>
            {errors.submit && (
              <div
                role="alert"
                className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              >
                {errors.submit}
              </div>
            )}

            <div className="mb-5">
              <label
                htmlFor="card-number"
                className="mb-2 block text-sm font-semibold text-slate-700"
              >
                Nomor Kartu
              </label>
              <input
                id="card-number"
                name="card_number"
                type="text"
                inputMode="numeric"
                autoComplete="username"
                maxLength={16}
                value={cardNumber}
                onChange={(event) => {
                  setCardNumber(event.target.value);
                  setErrors((current) => ({ ...current, cardNumber: undefined, submit: undefined }));
                }}
                placeholder="Masukkan nomor kartu"
                aria-invalid={Boolean(errors.cardNumber)}
                aria-describedby={errors.cardNumber ? 'card-number-error' : undefined}
                className={`w-full rounded-xl border px-4 py-3.5 text-slate-800 outline-none transition placeholder:text-slate-400 focus:ring-2 focus:ring-[#00908e]/25 ${
                  errors.cardNumber
                    ? 'border-red-400 focus:border-red-400'
                    : 'border-slate-300 focus:border-[#00908e]'
                }`}
              />
              {errors.cardNumber && (
                <p id="card-number-error" className="mt-1.5 text-xs text-red-600">
                  {errors.cardNumber}
                </p>
              )}
            </div>

            <div className="mb-7">
              <label
                htmlFor="birth-date"
                className="mb-2 block text-sm font-semibold text-slate-700"
              >
                Tanggal Lahir
              </label>
              <input
                id="birth-date"
                name="birth_date"
                type="date"
                max={new Date().toISOString().split('T')[0]}
                value={birthDate}
                onChange={(event) => {
                  setBirthDate(event.target.value);
                  setErrors((current) => ({ ...current, birthDate: undefined, submit: undefined }));
                }}
                aria-invalid={Boolean(errors.birthDate)}
                aria-describedby={errors.birthDate ? 'birth-date-error' : undefined}
                className={`w-full rounded-xl border bg-white px-4 py-3.5 text-slate-800 outline-none transition focus:ring-2 focus:ring-[#00908e]/25 ${
                  errors.birthDate
                    ? 'border-red-400 focus:border-red-400'
                    : 'border-slate-300 focus:border-[#00908e]'
                }`}
              />
              {errors.birthDate && (
                <p id="birth-date-error" className="mt-1.5 text-xs text-red-600">
                  {errors.birthDate}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#00908e] px-4 py-3.5 font-semibold text-white shadow-md transition hover:bg-[#007b7a] focus:outline-none focus:ring-4 focus:ring-[#00908e]/25 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading && (
                <span
                  aria-hidden="true"
                  className="h-5 w-5 animate-spin rounded-full border-2 border-white/40 border-t-white"
                />
              )}
              {loading ? 'Memproses...' : 'Masuk'}
            </button>
          </form>

          <p className="mt-6 text-center text-xs leading-5 text-slate-500">
            Data Anda digunakan hanya untuk memverifikasi kepesertaan BRI Life.
          </p>
        </section>
      </div>
    </main>
  );
};

export default Login;
